import React, { useState, useRef, useEffect } from "react";

/**
 * ShareProfileButton
 * Props:
 *   url      - The full URL to share (required)
 *   title    - Share title, e.g. "Mo Beauty Salon" (optional)
 *   label    - Button label (optional, default "Share Profile")
 */
function ShareProfileButton({ url, title = "Check me out on Ogestyle", label = "Share Profile" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Use native Web Share API on mobile if available — opens WhatsApp, Instagram, etc.
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // User cancelled — ignore
      }
      setOpen(false);
      return;
    }
    // Fallback: open dropdown
    setOpen((v) => !v);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 2000);
    });
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const options = [
    {
      label: copied ? "✓ Link Copied!" : "📋 Copy Link",
      onClick: handleCopy,
    },
    {
      label: "💬 WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    },
    {
      label: "✉️ Email",
      href: `mailto:?subject=${encodedTitle}&body=Check out my profile: ${encodedUrl}`,
    },
    {
      label: "📘 Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      label: "📸 Instagram",
      // Instagram doesn't support direct URL sharing; open profile page instead
      onClick: () => {
        navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
        });
        setOpen(false);
      },
      note: "(link copied — paste in bio)",
    },
    {
      label: "𝕏 X / Twitter",
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    },
  ];

  return (
    <div ref={menuRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={handleNativeShare}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "10px 18px",
          borderRadius: "10px",
          border: "none",
          background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
          color: "#fff",
          fontSize: "14px",
          fontWeight: "600",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(45,109,163,0.25)",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: "16px" }}>🔗</span>
        {label}
        {/* Only show chevron when native share not available */}
        {!navigator.share && (
          <span style={{ fontSize: "10px", opacity: 0.8 }}>▾</span>
        )}
      </button>

      {/* Dropdown — shown when native share is unavailable */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: "220px",
            zIndex: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px 16px",
              fontSize: "11px",
              fontWeight: "600",
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            Share your profile
          </div>
          {options.map((opt, i) => {
            const inner = (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: "500", color: "#111827" }}>
                  {opt.label}
                </span>
                {opt.note && (
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>{opt.note}</span>
                )}
              </div>
            );

            const sharedStyle = {
              display: "block",
              width: "100%",
              padding: "12px 16px",
              textAlign: "left",
              background: "none",
              border: "none",
              borderBottom: i < options.length - 1 ? "1px solid #f9fafb" : "none",
              cursor: "pointer",
              textDecoration: "none",
            };

            if (opt.href) {
              return (
                <a
                  key={opt.label}
                  href={opt.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={sharedStyle}
                  onClick={() => setOpen(false)}
                >
                  {inner}
                </a>
              );
            }
            return (
              <button key={opt.label} onClick={opt.onClick} style={sharedStyle}>
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ShareProfileButton;
